import { ComponentType, FC, FunctionComponent, LazyExoticComponent, Suspense } from 'react';
import { LoaderFunction, LoaderFunctionArgs } from 'react-router-dom';
import {
  bufferTime,
  concatMap,
  filter,
  firstValueFrom,
  Observable,
  Subject,
  take,
  tap,
} from 'rxjs';
import { moduleErrorBoundary as defaultErrorBoundary } from '../shared/components/errorBoundaries/moduleErrorBoundary.component';
import {
  Auth,
  authService,
  APP_MODULES,
  IUserAccessParams,
  initUserAccessParams,
} from '../shared/services/auth/auth.service';
import { companyConfigService } from '../shared/services/companyConfig/companyConfig.service';
import { IProtectedRoute, ProtectedRoute } from './protected.route';
import { isBrowserOutdated } from '../shared/helpers/browsersSupport/browserSupport.helper';
import { splashScreenService } from '../shared/components/loading/service/splashScreen.service.ts';
import { checkModuleAccess } from './helper.accessParams';
import { responseHandler } from '../shared/responseHandler/responseHandler';

class LazyUtil {
  private config: ILazyConfig = {};
  private taskSubject = new Subject<ITaskSubject>();
  private taskObservable: Observable<void>;
  private protectedRoute: FunctionComponent<IProtectedRoute> = ProtectedRoute;
  private queueLength = 0;
  private parentInitData: any;
  constructor() {
    this.taskObservable = this.taskSubject.pipe(
      bufferTime(5), // collect tasks within 5 ms
      filter((tasks) => !!tasks.length),
      tap((tasks) => tasks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))),
      concatMap((tasks) => tasks),
      concatMap((args) => this.executeTask(args)),
    );

    this.taskObservable.subscribe(); // starts queue processing
  }

  component(lazyComponent: ILazyConfig['lazyComponent']): LazyUtil {
    this.config!.lazyComponent = lazyComponent;
    return this;
  }
  loader(
    fn: ILazyConfig['loaderFn'],
    options?: { order?: number; appModule?: APP_MODULES },
  ): LazyUtil {
    const order = options?.order || 1;
    const appModule = options?.appModule;

    // Enqueue the task synchronously, before any await. React Router runs sibling
    // loaders (e.g. customerListLoader + customerTabLoader) in parallel; if we awaited
    // the auth/access checks here first, the siblings would enter the queue at different
    // times and could miss the shared bufferTime() batch — which breaks the order-based
    // sorting and the parent->child `parentInitData` handoff (symptom: getCustomerData
    // skipped right after login). The auth gate and module-access guard now run inside
    // executeTask so the queue order stays deterministic.
    this.config!.loaderFn = (args: LoaderFunctionArgs) => {
      this.queueLength++;
      return new Promise<any>((resolve, reject) => {
        this.taskSubject.next({ args, fn, resolve, reject, order, appModule });
      });
    };
    return this;
  }

  private executeTask = async ({ args, fn, resolve, reject, appModule }: ITaskSubject) => {
    let data: unknown;
    let producedData = false;
    try {
      const { status } = await firstValueFrom(authService.sub.isLoggedIn());
      if (Auth.loggedIn !== status) {
        window.location.replace('/login');
        resolve(false);
        return;
      }

      // Loader Access Guard
      if (appModule) {
        try {
          const userAccessParams = await authService.globHelpers.streamToPromise(
            authService.sub.getCachedUserAccessParams().pipe(
              responseHandler<IUserAccessParams>({
                errorReturnType: structuredClone(initUserAccessParams),
              }),
              take(1),
            ),
            () => {
              // we will not recall this function we get data from cache
            },
          );

          if (!checkModuleAccess(userAccessParams, appModule)) {
            // we will not call the original loader if the user has no access to the module
            resolve(null);
            return;
          }
        } catch (error) {
          console.error('Error checking module access:', error);
          // Continue to original loader if access check fails
        }
      }

      await firstValueFrom(companyConfigService.getConfigs().pipe(take(1)));
      data = await fn?.({
        ...args,
        ...(this.parentInitData && {
          context: { parentInitData: this.parentInitData },
        }),
      });
      producedData = true;
      resolve(data);
    } catch (error) {
      reject(error);
    } finally {
      // Centralized parentInitData cleanup: only carry data to the next pending
      // task when this task actually produced a loader result. Auth/access early
      // returns and exceptions clear it so stale parent data cannot leak into an
      // unrelated later navigation.
      if (producedData && this.queueLength > 1) {
        this.parentInitData = data;
      } else {
        this.parentInitData = null;
      }
      this.queueLength--;
      if (this.queueLength === 0) {
        splashScreenService.pub.off();
      }
    }
  };

  protected(customProtectedRoute?: FunctionComponent<any>): LazyUtil {
    if (customProtectedRoute) {
      this.config!.protected = customProtectedRoute;
    } else {
      this.config!.protected = this.protectedRoute;
    }
    return this;
  }

  errorBoundary(errorBoundaryComponent?: FunctionComponent): LazyUtil {
    if (errorBoundaryComponent) {
      this.config.errorBoundary = errorBoundaryComponent;
    } else {
      this.config.errorBoundary = defaultErrorBoundary;
    }
    return this;
  }
  result(): () => Promise<any> {
    const result: ILazyLoading = {};
    const config = { ...this.config };
    this.config = {};

    return async () => {
      if (config.lazyComponent !== undefined) {
        const LazyComponent = config.lazyComponent!;
        result.Component = config.protected
          ? () => {
              const Protected = config!.protected as FC<IProtectedRoute>;
              return (
                <Suspense>
                  <Protected>
                    <LazyComponent />
                  </Protected>
                </Suspense>
              );
            }
          : () => (
              <Suspense>
                <LazyComponent />
              </Suspense>
            );
      }
      if (!isBrowserOutdated() && typeof config.loaderFn === 'function') {
        result.loader = config.loaderFn;
      }
      if (config.errorBoundary) {
        result.ErrorBoundary = config.errorBoundary;
      }
      if (!config.loaderFn) {
        splashScreenService.pub.off();
      }
      return result;
    };
  }
}
interface ILazyConfig {
  lazyComponent?: LazyExoticComponent<ComponentType<any>>;
  loaderFn?: LoaderFunction;
  protected?: FC<IProtectedRoute>;
  errorBoundary?: FunctionComponent;
}
interface ILazyLoading {
  loader?: LoaderFunction;
  Component?: FunctionComponent;
  ErrorBoundary?: FunctionComponent;
}
interface ITaskSubject {
  args: LoaderFunctionArgs;
  fn: ILazyConfig['loaderFn'];
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
  order: number;
  appModule?: APP_MODULES;
}
export const lazyUtil = new LazyUtil();
